<template>
  <div>
    <b-message class="is-primary">
      {{ $t("datePickExplained") }}
    </b-message>
    <b-field>
      <b-datepicker
        v-model="avDates"
        inline
        :selectable-dates="editable ? null : [new Date(0)]"
        :mobile-native="false"
        :placeholder="$t('dateExplained')"
        icon="calendar-today"
        :min-date="minDate"
        :max-date="isSubscriptionTrialRunning ? trialMaxDate : maxDate"
        multiple
        :first-day-of-week="1"
        :locale="`${$i18n.locale}-${$i18n.locale.toUpperCase()}`"
      >
        <template>
          <div class="is-clearfix">
            <div style="color: #818181" class="is-pulled-right">
              {{ $t("timezone") }}:
              {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}
            </div>
          </div>
        </template>
      </b-datepicker>
    </b-field>
    <b-message class="is-primary">
      {{ $t("dayPickExplained") }}
    </b-message>
    <b-message v-if="eventType == 'livestream'" class="is-warning">
      {{ $t("isLivestreamTestWarning") }}
    </b-message>
    <b-message v-if="eventType == 'video'" class="is-warning">
      {{ $t("isVideoTestWarning") }}
    </b-message>
    <div v-for="(day, key) in eventTimes" :key="key" class="card mb-4 mt-4">
      <div class="card-content">
        <div class="level">
          <p class="subtitle">
            {{ $t(`days[${day.date.getDay()}]`) }}
            {{ new Intl.DateTimeFormat("de-DE").format(day.date) }}
          </p>
        </div>
        <b-field group-multiline grouped>
          <div class="control">
            <b-field :label="$t('startTime')" />
            <b-field>
              <template slot="message">
                <div class="is-clearfix">
                  <div style="color: #818181" class="is-pulled-right">
                    {{ $t("timezone") }}:
                    {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}
                  </div>
                </div>
              </template>
              <b-timepicker
                v-model="day.startTime"
                type="is-primary"
                inline
                :min-time="editable ? todayLimit(day.date) : day.startTime"
                :max-time="editable ? new Date(new Date(day.startTime).setHours(23, 59, 59, 999)) : day.startTime"
                :increment-minutes="15"
                hour-format="24"
              />
            </b-field>
          </div>
          <div class="control">
            <b-field :label="$t('endTime')" />
            <b-field>
              <b-timepicker
                v-model="day.endTime"
                :increment-minutes="15"
                type="is-primary"
                inline
                :min-time="editable ? day.startTime : day.endTime"
                :max-time="editable ? new Date(new Date(day.startTime).setHours(23, 59, 59, 999)) : day.endTime"
              />
              <template slot="message">
                <div class="is-clearfix">
                  <div style="color: #818181" class="is-pulled-right">
                    {{ $t("timezone") }}:
                    {{ Intl.DateTimeFormat().resolvedOptions().timeZone }}
                  </div>
                </div>
              </template>
            </b-field>
          </div>
        </b-field>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { db } from "@/firebase";
import isTrialRunning from "./utils";

const today = new Date();

export default {
  name: "Duration",
  components: {},
  props: {
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      minDate: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      maxDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 365),
      avDates: [],
      moment,
      organizationDoc: {},
    };
  },
  firestore() {
    return {
      organizationDoc: db.doc(`organizations/${this.$route.params.organizationId}`),
    };
  },
  computed: {
    trialMaxDate() {
      const organizationDoc = JSON.parse(JSON.stringify(this.organizationDoc));

      if (!organizationDoc?.trialSubscription || !organizationDoc.trialSubscription?.end) return this.maxDate;

      return new Date(organizationDoc.trialSubscription?.end);
    },
    isSubscriptionTrialRunning() {
      const organizationDoc = JSON.parse(JSON.stringify(this.organizationDoc));

      return isTrialRunning(organizationDoc);
    },
    eventTimes: {
      get() {
        return this.$store.state.draft.eventTimes;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["eventTimes", value]);
      },
    },
    eventType: {
      get() {
        return this.$store.state.draft.eventType;
      },
      set(value) {
        if (value == "external" || value == "audio") {
          this.isVideoEvent = false;
        } else {
          this.isVideoEvent = true;
        }
        this.$store.commit("SET_DRAFT_FIELD", ["eventType", value]);
      },
    },
    livestreamType: {
      get() {
        return this.$store.state.draft.livestreamType;
      },
      set(value) {
        this.$store.commit("SET_DRAFT_FIELD", ["livestreamType", value]);
      },
    },
  },
  watch: {
    // fill datepicker when eventTimes is loaded in from draft
    eventTimes: {
      deep: true,
      handler() {
        if (this.eventTimes.length) {
          this.$store.commit("SET_DRAFT_FIELD", ["startTime", this.eventTimes[0].startTime]);
          this.$store.commit("SET_DRAFT_FIELD", [
            "endTime",
            new Date(this.eventTimes[this.eventTimes.length - 1].endTime),
          ]);
        }
        if (this.avDates.length == 0) {
          this.eventTimes
            .map((y) => y.date)
            .forEach((y) => {
              this.avDates.push(y);
            });
        }
      },
    },
    avDates(avDates) {
      let dates = this.eventTimes.map((y) => moment(y.date).format("YYYY-MM-DD"));
      let newDates = avDates.filter((y) => !dates.includes(moment(y).format("YYYY-MM-DD")));

      if (newDates.length > 0) {
        newDates.forEach((date) => {
          this.eventTimes.push({
            date: date,
            startTime: this.todayLimit(date, 8, "start"),
            endTime: this.todayLimit(date, 16, "end"),
          });
        });
      } else {
        let formattedAvDates = avDates.map((y) => moment(y).format("YYYY-MM-DD"));
        let removedDates = dates.filter((y) => !formattedAvDates.includes(y));
        this.eventTimes = this.eventTimes.filter((y) => !removedDates.includes(moment(y.date).format("YYYY-MM-DD")));
      }
      this.eventTimes.sort((a, b) => a.date.getTime().toString().localeCompare(b.date.getTime().toString()));
    },
  },
  methods: {
    setTime(date, hour, minute = 0, second = 0, millisecond = 0) {
      let time = moment(date).toDate();

      time.setMilliseconds(millisecond);
      time.setSeconds(second);
      time.setMinutes(minute);
      time.setHours(hour);

      return new Date(time);
    },
    // checks if date is today, if yes return the current hour as limit
    // else return just the date but replace hour with a given replacement
    todayLimit(date, replace = 0, type = "start") {
      let currentTime = moment();
      if (currentTime.format("YYYY-MM-DD") == moment(date).format("YYYY-MM-DD") && type == "start") {
        return this.setTime(currentTime.toDate(), currentTime.hours());
      } else if (currentTime.format("YYYY-MM-DD") == moment(date).format("YYYY-MM-DD") && type == "end") {
        return this.setTime(currentTime.toDate(), currentTime.add(1, "hours").hours());
      }
      return this.setTime(date, replace);
    },
  },
};
</script>

<style scoped>
.timepicker {
  min-width: 200px;
}
</style>
